import { useCallback, useEffect, useState } from 'react';
import './app-shell.css';
import { Footer } from './Footer';
import { PageHeader } from './PageHeader';
import { Sidebar } from './Sidebar';
import { cn } from './ui/utils';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebarCollapsed';

const readInitialCollapsed = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export function AppLayout({
  title,
  subtitle,
  icon,
  headerActions,
  children,
  mainClassName,
  showFooter = true,
}) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readInitialCollapsed);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncSidebarState = () => {
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(false);
      }
    };

    syncSidebarState();
    window.addEventListener('resize', syncSidebarState);

    return () => {
      window.removeEventListener('resize', syncSidebarState);
    };
  }, []);

  const toggleSidebarCollapsed = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      } catch {
        // localStorage indisponivel (modo privado) — ignora persistencia
      }
      return next;
    });
  }, []);

  return (
    <div
      className="app-shell min-h-screen bg-slate-50"
      data-sidebar-open={isSidebarOpen ? 'true' : 'false'}
      data-sidebar-collapsed={isSidebarCollapsed ? 'true' : 'false'}
    >
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapsed={toggleSidebarCollapsed}
      />

      <div className="app-shell__content min-h-screen flex flex-col">
        <PageHeader
          title={title}
          subtitle={subtitle}
          icon={icon}
          onMenuToggle={() => setIsSidebarOpen((current) => !current)}
          isSidebarOpen={isSidebarOpen}
        >
          {headerActions}
        </PageHeader>

        <main className={cn('flex-1 min-w-0 p-4 sm:p-6 min-[1200px]:p-8', mainClassName)}>
          {children}
        </main>

        {showFooter && <Footer />}
      </div>
    </div>
  );
}
